import MetricBox from "components-for/display/metric-box";
import {
    faCheckCircle,
    faStarHalfAlt,
    faUserClock,
    faUserChart,
    faStopwatch,
    faUsers,
    faStar,
    faUserCheck,
} from "@fortawesome/pro-duotone-svg-icons";
import { FunctionComponent, memo } from "react";
import { IProps } from "./agent-interaction-metrics.types";
import { parseUrlArray } from "@edgetier/utilities";
import { TimePeriodValue, useQueryParameters } from "@edgetier/client-components";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useInteractionMetricsSummary } from "hooks-for/interaction-metrics/use-interaction-metrics-summaries";
import { EXPERIENCE_SCORE_METRIC_ENABLED } from "constants/feature-flags";
import { InteractionMetric } from "@edgetier/watchtower-types";
import { checkMetricAvailable } from "utilities/check-metric-available";
import "./agent-interaction-metrics.scss";

/**
 * Show summary metrics for an agent's interaction metrics.
 * @param props.defaultEndDateTime   Closing date in range to search.
 * @param props.defaultStartDateTime Opening date in range to search.
 * @param props.interactionMetricsAvailable Interaction metrics available
 * @returns                          Several metrics.
 */
const AgentInteractionMetrics: FunctionComponent<IProps> = ({
    defaultEndDateTime,
    defaultStartDateTime,
    interactionMetricsAvailable,
}) => {
    const otherFilters = useInteractionQueryParameters({
        defaults: { startDateTime: defaultStartDateTime, endDateTime: defaultEndDateTime },
    });

    const { comparisonAgentId, reviewAgentId } = useQueryParameters({
        comparisonAgentId: parseUrlArray,
        reviewAgentId: String,
    });

    // Request metric summaries for the review agent.
    const { data: agentData, isLoading: isAgentDataLoading } = useInteractionMetricsSummary({
        agentId: reviewAgentId,
        ...otherFilters,
    });

    // Request metric summaries for the comparison agents.
    const { data: overallData, isLoading: isOverallDataLoading } = useInteractionMetricsSummary({
        agentId: comparisonAgentId,
        ...otherFilters,
    });

    return (
        <>
            {checkMetricAvailable(interactionMetricsAvailable, InteractionMetric.HandlingTime) && (
                <MetricBox
                    value={agentData?.meanHandlingTimeSeconds ?? null}
                    icon={faStopwatch}
                    isLoading={isAgentDataLoading || isOverallDataLoading}
                    options={{ maximumFractionDigits: 1 }}
                    comparisonValue={overallData?.meanHandlingTimeSeconds ?? null}
                    renderValue={(value, otherProps) => <TimePeriodValue seconds={value} {...otherProps} />}
                    title="AHT"
                    tooltipContent="Average agent handling time of all interactions"
                />
            )}

            {checkMetricAvailable(interactionMetricsAvailable, InteractionMetric.AverageResponseTime) && (
                <MetricBox
                    value={agentData?.meanResponseSeconds ?? null}
                    icon={faUserClock}
                    isLoading={isAgentDataLoading || isOverallDataLoading}
                    options={{ maximumFractionDigits: 1 }}
                    comparisonValue={overallData?.meanResponseSeconds ?? null}
                    renderValue={(value, otherProps) => <TimePeriodValue seconds={value} {...otherProps} />}
                    title="Average Response Time"
                    tooltipContent="The amount of time on average between customer messages and agent responses"
                />
            )}
            {checkMetricAvailable(interactionMetricsAvailable, InteractionMetric.AgentFirstResponseTime) && (
                <MetricBox
                    value={agentData?.meanAgentFirstResponseTime ?? null}
                    icon={faStopwatch}
                    isLoading={isAgentDataLoading || isOverallDataLoading}
                    options={{ maximumFractionDigits: 1 }}
                    comparisonValue={overallData?.meanAgentFirstResponseTime ?? null}
                    renderValue={(value, otherProps) => <TimePeriodValue seconds={value} {...otherProps} />}
                    title="Average Agent First Response Time"
                    tooltipContent="The amount of time on average an agent will response"
                />
            )}

            {checkMetricAvailable(interactionMetricsAvailable, InteractionMetric.NetPromoterScore) && (
                <MetricBox
                    value={agentData?.netPromoterScore ?? null}
                    icon={faStarHalfAlt}
                    isLoading={isAgentDataLoading || isOverallDataLoading}
                    options={{ maximumFractionDigits: 0 }}
                    comparisonValue={overallData?.netPromoterScore ?? null}
                    title="NPS"
                    tooltipContent={
                        <>
                            <div>Net promoter score</div>
                            (promoters &minus; detractors)
                        </>
                    }
                />
            )}

            {checkMetricAvailable(interactionMetricsAvailable, InteractionMetric.CustomerSatisfaction) && (
                <MetricBox
                    value={
                        typeof agentData?.customerSatisfactionScore === "number"
                            ? 100 * agentData?.customerSatisfactionScore
                            : null
                    }
                    icon={faUsers}
                    isLoading={isAgentDataLoading || isOverallDataLoading}
                    options={{ maximumFractionDigits: 0 }}
                    comparisonValue={
                        typeof overallData?.customerSatisfactionScore === "number"
                            ? 100 * overallData?.customerSatisfactionScore
                            : null
                    }
                    title="CSAT"
                    tooltipContent={
                        <>
                            <div>Customer satisfaction score</div>
                            (count of 4 and 5 ratings) &divide; (total ratings)
                        </>
                    }
                    unit="%"
                />
            )}

            {checkMetricAvailable(interactionMetricsAvailable, InteractionMetric.QueryResolution) && (
                <MetricBox
                    value={
                        typeof agentData?.queryResolutionRate === "number" ? 100 * agentData?.queryResolutionRate : null
                    }
                    icon={faCheckCircle}
                    isLoading={isAgentDataLoading || isOverallDataLoading}
                    options={{ maximumFractionDigits: 0 }}
                    comparisonValue={
                        typeof overallData?.queryResolutionRate === "number"
                            ? 100 * overallData?.queryResolutionRate
                            : null
                    }
                    title="Resolution Rate"
                    tooltipContent="The percentage of customer queries that were resolved"
                    unit="%"
                />
            )}

            {checkMetricAvailable(interactionMetricsAvailable, InteractionMetric.AgentSatisfaction) && (
                <MetricBox
                    value={agentData?.meanAgentSatisfaction ?? null}
                    icon={faUserCheck}
                    isLoading={isAgentDataLoading || isOverallDataLoading}
                    options={{ maximumFractionDigits: 1 }}
                    comparisonValue={overallData?.meanAgentSatisfaction ?? null}
                    title="Average Agent Satisfaction"
                    tooltipContent="Average Agent Satisfaction"
                />
            )}

            {checkMetricAvailable(interactionMetricsAvailable, InteractionMetric.AgentScore) && (
                <MetricBox
                    value={agentData?.meanAgentScore ?? null}
                    icon={faStar}
                    isLoading={isAgentDataLoading || isOverallDataLoading}
                    options={{ maximumFractionDigits: 1 }}
                    comparisonValue={overallData?.meanAgentScore ?? null}
                    title="Average Agent Score"
                    tooltipContent="Average Agent Score"
                />
            )}

            {checkMetricAvailable(
                interactionMetricsAvailable,
                InteractionMetric.ExperienceScore,
                EXPERIENCE_SCORE_METRIC_ENABLED
            ) && (
                <MetricBox
                    value={agentData?.meanExperienceScore ?? null}
                    icon={faUserChart}
                    isLoading={isAgentDataLoading || isOverallDataLoading}
                    options={{ maximumFractionDigits: 1 }}
                    comparisonValue={overallData?.meanExperienceScore ?? null}
                    title="Average Experience Score"
                    tooltipContent={
                        <div>
                            Predicted score of a customer's experience based on factors such as sentiment and handling
                            time (scale 0&ndash;10)
                        </div>
                    }
                />
            )}
        </>
    );
};

export default memo(AgentInteractionMetrics);
